import { useEffect, useState } from 'react';
import {
  fetchUpcomingAgendaEvents,
  formatAgendaRange,
  type AgendaEvent,
} from '@/features/agenda/agendaEvents';
import { useI18n } from '@/features/i18n/I18nProvider';
import { useToast } from '@/features/ui/toast/useToast';

function AgendaEventModal({ event, onClose }: { event: AgendaEvent; onClose: () => void }) {
  const { locale } = useI18n();

  return (
    <div className="nk-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="nk-modal"
        role="dialog"
        aria-modal
        aria-labelledby="agenda-event-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 id="agenda-event-title">{event.title}</h3>
        {event.subject ? (
          <p className="muted" style={{ marginTop: 0 }}>
            {event.subject}
          </p>
        ) : null}
        <p style={{ marginTop: '0.75rem' }}>
          <strong>{locale === 'en' ? 'Schedule' : 'Horaires'}</strong> :{' '}
          {formatAgendaRange(event.starts_at, event.ends_at, locale)}
        </p>
        {event.instructions ? (
          <p style={{ whiteSpace: 'pre-wrap', marginTop: '0.75rem' }}>{event.instructions}</p>
        ) : null}
        <div className="nk-modal-actions">
          <button type="button" className="btn btn-primary" onClick={onClose}>
            {locale === 'en' ? 'Close' : 'Fermer'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function AgendaEventsPanel() {
  const { locale } = useI18n();
  const toast = useToast();
  const [events, setEvents] = useState<AgendaEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<AgendaEvent | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void fetchUpcomingAgendaEvents()
      .then((rows) => {
        if (!cancelled) setEvents(rows);
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(err instanceof Error ? err.message : 'Impossible de charger les événements.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [toast]);

  return (
    <>
      <section className="nk-panel">
        <h3 style={{ marginTop: 0 }}>{locale === 'en' ? 'Events' : 'Événements'}</h3>
        <p className="muted" style={{ marginTop: 0, marginBottom: '0.65rem', fontSize: '0.88rem' }}>
          {locale === 'en'
            ? 'Webinars, workshops and ISBD appointments.'
            : 'Webinaires, ateliers et rendez-vous ISBD.'}
        </p>
        {loading ? (
          <p className="muted" style={{ margin: 0, fontSize: '0.88rem' }}>
            {locale === 'en' ? 'Loading…' : 'Chargement…'}
          </p>
        ) : events.length === 0 ? (
          <p className="muted" style={{ margin: 0, fontSize: '0.88rem' }}>
            {locale === 'en' ? 'No upcoming events.' : 'Aucun événement à venir pour le moment.'}
          </p>
        ) : (
          <ul className="nk-community-list">
            {events.map((ev) => (
              <li key={ev.id}>
                <button
                  type="button"
                  className="nk-community-item nk-community-item--button"
                  onClick={() => setSelected(ev)}
                >
                  <span className="nk-community-icon is-events" aria-hidden>
                    📅
                  </span>
                  <div>
                    <strong>{ev.title}</strong>
                    <span>
                      {formatAgendaRange(ev.starts_at, ev.ends_at, locale)}
                      {ev.subject ? ` · ${ev.subject}` : ''}
                    </span>
                  </div>
                  <span className="nk-chevron" aria-hidden>
                    ›
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {selected ? <AgendaEventModal event={selected} onClose={() => setSelected(null)} /> : null}
    </>
  );
}
